/**
 * Dashboard Moderno - JurisPrazo V2
 * Interface unificada e moderna inspirada no sistema antigo do usuário
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Settings, LogOut, Search, RefreshCw, AlertCircle, Download, Maximize2, Minimize2,
  FileText, User, ExternalLink, Calendar, Clock, Copy, Printer, AlertTriangle
} from 'lucide-react';

// Novos componentes
import DashboardHero from '../components/DashboardHero';
import { PrazoMetricCard } from '../components/MetricCard';
import PrazoCard from '../components/PrazoCard';
import CalendarioPrazos from '../components/CalendarioPrazos';
import EditarPrazoModal from '../components/EditarPrazoModal';
import ComunicacaoCard from '../components/ComunicacaoCard';
import Pagination from '../components/Pagination';
import SyncModal from '../components/SyncModal';

// Componentes existentes
import { Button, Input, Modal } from '../components/ui';
import { APP_CONFIG, THEME_TRIBUNAL_MODERNO } from '../config/theme';
import OnboardingWizard from '../components/OnboardingWizard';

// Serviços
import { FirebaseService } from '../services/firebaseService';
import { SyncService } from '../services/syncService';
import PrazoService from '../services/prazoService';
import ComunicacaoService from '../services/comunicacaoService';

// Hooks
import { useSmartRefresh } from '../hooks/useSmartRefresh';
import performanceMonitor from '../utils/performanceMonitor';
import SyncHistoryService from '../services/syncHistoryService';
import DateFilterView from '../components/DateFilterView';

const DashboardPage = ({ currentUser, onSignOut, onNavigate, shouldOpenSyncModal, onSyncModalOpened }) => {
  // Estados principais
  const [prazos, setPrazos] = useState([]);
  const [comunicacoes, setComunicacoes] = useState([]);
  const [metricas, setMetricas] = useState({});
  const [metricasComunicacoes, setMetricasComunicacoes] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Cache para dados que não mudam frequentemente
  const [cacheProfile, setCacheProfile] = useState(null);
  const [cacheProfileExpiry, setCacheProfileExpiry] = useState(null);

  // Estados de filtros e abas
  const [abaAtiva, setAbaAtiva] = useState('prazos'); // 'prazos' ou 'comunicacoes'
  const [filtroAtivo, setFiltroAtivo] = useState('todos'); // todos, vencidos, hoje, esta_semana para prazos / todos, nao_lidas para comunicações
  const [searchTerm, setSearchTerm] = useState('');

  // Estados de modais
  const [showCalendario, setShowCalendario] = useState(false);
  const [showPrazoDetails, setShowPrazoDetails] = useState(false);
  const [showEditarPrazo, setShowEditarPrazo] = useState(false);
  const [prazoSelecionado, setPrazoSelecionado] = useState(null);
  const [conteudoExpandido, setConteudoExpandido] = useState(false);

  // Estados para sincronização
  const [syncStats, setSyncStats] = useState(null);
  
  // Estados para visualização por data
  const [showDateView, setShowDateView] = useState(false);

  // Estados de sincronização
  const [loadingSync, setLoadingSync] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Estados do Onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Configurar refresh inteligente (muito conservador para performance)
  const { smartRefresh } = useSmartRefresh(
    async () => {
      if (currentUser?.uid && !currentUser.isDemoUserManual) {
        await loadDashboardData();
      }
    },
    {
      minInterval: 600000, // 10 minutos entre refreshes (era 2min)
      refreshOnFocus: false, // Não recarregar ao focar na aba
      refreshOnMount: false, // Não refresh automático no mount
      context: 'Dashboard'
    }
  );

  // Handler para refresh manual
  const handleManualRefresh = async () => {
    setLoading(true);
    try {
      await smartRefresh();
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados quando o usuário estiver logado
  useEffect(() => {
    if (currentUser?.uid && !currentUser.isDemoUserManual) {
      loadDashboardData();
      checkOnboardingStatus();
    } else if (currentUser?.isDemoUserManual) {
      loadDemoData();
    }
  }, [currentUser]);

  // Abrir modal de sync quando solicitado externamente (via AppLayout)
  useEffect(() => {
    if (shouldOpenSyncModal) {
      setShowSyncModal(true);
      onSyncModalOpened && onSyncModalOpened();
    }
  }, [shouldOpenSyncModal, onSyncModalOpened]);

  // Verificar se usuário precisa ver o onboarding
  const checkOnboardingStatus = async () => {
    if (!currentUser?.uid) return;

    try {
      // Verificar se já foi marcado para não mostrar mais
      const neverShowAgain = localStorage.getItem(`onboarding_never_show_${currentUser.uid}`);
      if (neverShowAgain === 'true') {
        console.log('🙈 Usuário optou por não ver mais o onboarding');
        return;
      }

      // Verificar cache do perfil (cache por 30 minutos)
      const now = Date.now();
      let profile = null;
      
      if (cacheProfile && cacheProfileExpiry && now < cacheProfileExpiry) {
        console.log('📦 Usando perfil do cache');
        profile = cacheProfile;
      } else {
        console.log('🔍 Buscando perfil do Firebase');
        profile = await FirebaseService.getUserProfile(currentUser.uid);
        
        // Atualizar cache
        setCacheProfile(profile);
        setCacheProfileExpiry(now + 30 * 60 * 1000); // 30 minutos
      }
      
      setUserProfile(profile);

      // Se o onboarding não foi concluído, mostrar o wizard
      if (!profile?.onboardingCompleted) {
        console.log('🎯 Mostrando onboarding para novo usuário');
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('❌ Erro ao verificar status do onboarding:', error);
    }
  };

  // Carregar dados do dashboard
  const loadDashboardData = async () => {
    if (!currentUser?.uid) {
      console.log('❌ loadDashboardData: Usuário não logado');
      return;
    }

    const perfOp = performanceMonitor.startOperation('Dashboard Load');
    
    try {
      setLoading(true);
      console.log('🔄 Carregando dados do dashboard...');

      // Carregar dados em paralelo com timeout
      console.log('📊 Buscando prazos e comunicações...');
      const [userPrazos, userComunicacoes] = await Promise.race([
        Promise.all([
          PrazoService.getUserPrazos(currentUser.uid),
          ComunicacaoService.getUserComunicacoes(currentUser.uid)
        ]),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout: Carregamento demorou mais de 30 segundos')), 30000)
        )
      ]);

      console.log(`📊 Dados carregados: ${userPrazos.length} prazos, ${userComunicacoes.length} comunicações`);

      setPrazos(userPrazos);
      setComunicacoes(userComunicacoes);

      // 📊 APENAS ANÁLISE DOS DADOS (sem processamento IA automático)
      console.log(`📊 ANÁLISE DAS COMUNICAÇÕES:`);
      console.log(`📧 Total comunicações: ${userComunicacoes.length}`);
      
      const semProcessamento = userComunicacoes.filter(c => !c.processada);
      const semIA = userComunicacoes.filter(c => !c.aiAnalysis);
      
      console.log(`🔍 Sem processamento: ${semProcessamento.length}`);
      console.log(`🤖 Sem análise IA: ${semIA.length}`);
      
      if (semProcessamento.length > 0) {
        console.log(`💡 DICA: Use a sincronização para processar ${semProcessamento.length} comunicações pendentes`);
        console.log(`🚀 PERFORMANCE: Dashboard otimizado - processamento IA apenas na sincronização`);
      }

      // Carregar métricas finais (após possível processamento automático)
      const finalMetricas = await PrazoService.getMetricasPrazos(currentUser.uid);
      setMetricas(finalMetricas);

      // Calcular métricas finais de comunicações (usar dados atuais)
      const comunicacoesFinal = comunicacoes.length > 0 ? comunicacoes : userComunicacoes;
      const comunicacoesProcessadasFinal = comunicacoesFinal.filter(c => c.processada);
      const comunicacoesPendentesFinal = comunicacoesFinal.filter(c => !c.processada);
      const comunicacoesNaoLidasFinal = comunicacoesFinal.filter(c => !c.isRead);

      setMetricasComunicacoes({
        total: comunicacoesFinal.length,
        processadas: comunicacoesProcessadasFinal.length,
        pendentes: comunicacoesPendentesFinal.length,
        naoLidas: comunicacoesNaoLidasFinal.length,
        comPrazo: comunicacoesProcessadasFinal.filter(c => c.temPrazo).length
      });

      // Carregar estatísticas de sincronização
      try {
        const stats = await SyncHistoryService.getSyncStats(currentUser.uid);
        setSyncStats(stats);
        console.log('📊 Estatísticas de sync:', stats);
      } catch (error) {
        console.error('❌ Erro ao carregar estatísticas de sync:', error);
      }

      console.log(`✅ Dashboard carregado: ${prazos.length} prazos, ${comunicacoesFinal.length} comunicações`);
      console.log('📊 Métricas prazos:', finalMetricas);
      console.log('📧 Métricas comunicações:', {
        total: comunicacoesFinal.length,
        processadas: comunicacoesProcessadasFinal.length,
        pendentes: comunicacoesPendentesFinal.length,
        naoLidas: comunicacoesNaoLidasFinal.length
      });

    } catch (error) {
      console.error('❌ Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
      perfOp?.end();
    }
  };

  // Carregar dados de demonstração
  const loadDemoData = () => {
    const hoje = new Date();
    const amanha = new Date(hoje);
    amanha.setDate(amanha.getDate() + 1);
    const proximaSemana = new Date(hoje);
    proximaSemana.setDate(proximaSemana.getDate() + 7);
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    const semanaPassada = new Date(hoje);
    semanaPassada.setDate(semanaPassada.getDate() - 7);

    const prazosDemo = [
      {
        id: 'demo1',
        fonte: 'comunicacao',
        numeroProcesso: '0012345-67.2023.8.22.0001',
        cliente: 'Empresa Demo Ltda',
        tribunal: 'TJRO',
        tipo: 'contestação',
        tipoAto: 'citação',
        descricao: 'Contestar ação de cobrança',
        dias: 15,
        diasUteis: false,
        dataInicio: new Date(hoje.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: hoje.toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: 0,
        urgencia: 'hoje',
        status: 'pendente',
        observacoes: 'Verificar se há documentos em anexo',
        textoCompleto: 'CITAÇÃO - Fica V.Sa. citado(a) para, no prazo de 15 (quinze) dias, contestar a presente ação de cobrança, sob pena de revelia e confissão quanto à matéria de fato.',
        analisadoPorIA: true,
        acoesSugeridas: ['Preparar contestação', 'Verificar documentos', 'Analisar fundamentos'],
        tags: ['urgente', 'cobrança', 'citação'],
        dataAnalise: new Date().toISOString()
      },
      {
        id: 'demo2',
        fonte: 'processo',
        numeroProcesso: '0098765-43.2023.8.22.0002',
        cliente: 'Cliente Teste',
        tribunal: 'TRT23',
        tipo: 'recurso',
        tipoAto: 'sentença',
        descricao: 'Recurso ordinário trabalhista',
        dias: 8,
        diasUteis: true,
        dataInicio: new Date(hoje.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: proximaSemana.toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: 7,
        urgencia: 'esta_semana',
        status: 'pendente',
        observacoes: '',
        textoCompleto: 'SENTENÇA - Julgo procedente o pedido. Condeno a ré ao pagamento de indenização. Prazo para recurso: 8 dias úteis.',
        analisadoPorIA: false,
        acoesSugeridas: ['Elaborar recurso', 'Analisar fundamentos da sentença'],
        tags: ['trabalhista', 'recurso'],
        dataAnalise: new Date().toISOString()
      },
      // Adicionando prazos concluídos para demonstração
      {
        id: 'demo3',
        fonte: 'comunicacao',
        numeroProcesso: '0055555-11.2023.8.22.0003',
        cliente: 'Cliente Concluído A',
        tribunal: 'TJRO',
        tipo: 'manifestação',
        tipoAto: 'intimação',
        descricao: 'Manifestação sobre documentos',
        dias: 10,
        diasUteis: false,
        dataInicio: new Date(hoje.getTime() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: new Date(hoje.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: -10,
        urgencia: 'vencido',
        status: 'concluido',
        dataConclusao: new Date(hoje.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
        observacoes: 'Manifestação protocolada com sucesso',
        textoCompleto: 'INTIMAÇÃO - Fica V.Sa. intimado para se manifestar sobre os documentos juntados aos autos.',
        analisadoPorIA: true,
        acoesSugeridas: [],
        tags: ['manifestação', 'documentos'],
        dataAnalise: new Date().toISOString()
      },
      {
        id: 'demo4',
        fonte: 'processo',
        numeroProcesso: '0066666-22.2023.8.22.0004',
        cliente: 'Cliente Concluído B',
        tribunal: 'TRT23',
        tipo: 'petição',
        tipoAto: 'despacho',
        descricao: 'Petição inicial protocolada',
        dias: 30,
        diasUteis: false,
        dataInicio: new Date(hoje.getTime() - 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: new Date(hoje.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: -5,
        urgencia: 'vencido',
        status: 'concluido',
        dataConclusao: new Date(hoje.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        observacoes: 'Petição protocolada dentro do prazo',
        textoCompleto: 'DESPACHO - Defiro o pedido de prazo para protocolo da petição inicial.',
        analisadoPorIA: false,
        acoesSugeridas: [],
        tags: ['petição', 'inicial'],
        dataAnalise: new Date().toISOString()
      },
      // 🔥 PRAZOS VENCIDOS PARA TESTAR
      {
        id: 'demo_vencido_1',
        fonte: 'comunicacao',
        numeroProcesso: '0099999-88.2023.8.22.0999',
        cliente: 'Cliente com Prazo Vencido',
        tribunal: 'TJRO',
        tipo: 'recurso',
        tipoAto: 'sentença',
        descricao: 'Recurso de apelação (VENCIDO)',
        dias: 15,
        diasUteis: false,
        dataInicio: semanaPassada.toISOString().split('T')[0],
        dataVencimento: ontem.toISOString().split('T')[0],
        dataPublicacao: semanaPassada.toISOString().split('T')[0],
        diasRestantes: -1,
        urgencia: 'vencido',
        status: 'pendente',
        observacoes: 'Prazo venceu ontem!',
        textoCompleto: 'SENTENÇA - Julgo procedente o pedido. Prazo para recurso: 15 dias.',
        analisadoPorIA: true,
        acoesSugeridas: ['Verificar prazo em dobro', 'Analisar jurisprudência'],
        tags: ['urgente', 'vencido', 'recurso'],
        dataAnalise: new Date().toISOString()
      },
      {
        id: 'demo_vencido_2',
        fonte: 'comunicacao',
        numeroProcesso: '0088888-77.2023.8.22.0888',
        cliente: 'Outro Cliente Vencido',
        tribunal: 'TJRO',
        tipo: 'contestacao',
        tipoAto: 'citação',
        descricao: 'Contestação (VENCIDO há 3 dias)',
        dias: 15,
        diasUteis: true,
        dataInicio: new Date(hoje.getTime() - 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: new Date(hoje.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: -3,
        urgencia: 'vencido',
        status: 'pendente',
        observacoes: 'Prazo vencido há 3 dias - URGENTE!',
        textoCompleto: 'CITAÇÃO - Para contestar no prazo de 15 dias úteis.',
        analisadoPorIA: true,
        acoesSugeridas: ['Contatar cliente URGENTE', 'Verificar revelia'],
        tags: ['urgente', 'vencido', 'contestacao'],
        dataAnalise: new Date().toISOString()
      },
      // 🚨 PRAZO PARA HOJE
      {
        id: 'demo_hoje',
        fonte: 'comunicacao',
        numeroProcesso: '0077777-66.2023.8.22.0777',
        cliente: 'Cliente Prazo Hoje',
        tribunal: 'TJRO',
        tipo: 'manifestacao',
        tipoAto: 'despacho',
        descricao: 'Manifestação sobre despacho (VENCE HOJE)',
        dias: 10,
        diasUteis: false,
        dataInicio: new Date(hoje.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: hoje.toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: 0,
        urgencia: 'hoje',
        status: 'pendente',
        observacoes: 'VENCE HOJE - Último dia!',
        textoCompleto: 'DESPACHO - Manifeste-se no prazo de 10 dias.',
        analisadoPorIA: true,
        acoesSugeridas: ['Protocolar hoje', 'Preparar petição'],
        tags: ['urgente', 'hoje', 'manifestacao'],
        dataAnalise: new Date().toISOString()
      },
      // 🟡 PRAZOS ESTA SEMANA
      {
        id: 'demo_semana_1',
        fonte: 'comunicacao',
        numeroProcesso: '0066666-55.2023.8.22.0666',
        cliente: 'Cliente Semana 1',
        tribunal: 'TJRO',
        tipo: 'recurso',
        tipoAto: 'acórdão',
        descricao: 'Recurso Especial (vence em 3 dias)',
        dias: 15,
        diasUteis: true,
        dataInicio: new Date(hoje.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: new Date(hoje.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: 3,
        urgencia: 'esta_semana',
        status: 'pendente',
        observacoes: 'Vence em 3 dias - preparar recurso',
        textoCompleto: 'ACÓRDÃO - Prazo para recurso especial: 15 dias úteis.',
        analisadoPorIA: true,
        acoesSugeridas: ['Analisar viabilidade', 'Preparar minuta'],
        tags: ['recurso', 'especial', 'esta_semana'],
        dataAnalise: new Date().toISOString()
      },
      {
        id: 'demo_semana_2',
        fonte: 'comunicacao',
        numeroProcesso: '0055555-44.2023.8.22.0555',
        cliente: 'Cliente Semana 2',
        tribunal: 'TJRO',
        tipo: 'impugnacao',
        tipoAto: 'sentença',
        descricao: 'Impugnação ao cumprimento (vence em 5 dias)',
        dias: 15,
        diasUteis: false,
        dataInicio: new Date(hoje.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataVencimento: new Date(hoje.getTime() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataPublicacao: new Date(hoje.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        diasRestantes: 5,
        urgencia: 'esta_semana',
        status: 'pendente',
        observacoes: 'Vence em 5 dias - analisar cálculos',
        textoCompleto: 'SENTENÇA - Prazo para impugnação: 15 dias.',
        analisadoPorIA: true,
        acoesSugeridas: ['Revisar cálculos', 'Preparar impugnação'],
        tags: ['impugnacao', 'calculo', 'esta_semana'],
        dataAnalise: new Date().toISOString()
      }
    ];

    const totalPrazos = prazosDemo.length;
    const prazosAtivos = prazosDemo.filter(p => p.status !== 'concluido');
    const prazosConcluidos = prazosDemo.filter(p => p.status === 'concluido');
    const taxaCumprimento = totalPrazos > 0 ? Math.round((prazosConcluidos.length / totalPrazos) * 100) : 0;

    // Debug logs para verificar o cálculo
    console.log('📊 Dados Demo - Taxa de Conclusão:');
    console.log('Total de prazos:', totalPrazos);
    console.log('Prazos concluídos:', prazosConcluidos.length);
    console.log('Prazos ativos:', prazosAtivos.length);
    console.log('Taxa calculada:', taxaCumprimento + '%');
    console.log('Prazos concluídos:', prazosConcluidos.map(p => ({ id: p.id, status: p.status, cliente: p.cliente })));

    setPrazos(prazosDemo);
    
    // Calcular métricas precisas
    const prazosHoje = prazosAtivos.filter(p => p.urgencia === 'hoje').length;
    const prazosEstaSemana = prazosAtivos.filter(p => p.urgencia === 'esta_semana').length;
    const prazosVencidos = prazosAtivos.filter(p => p.urgencia === 'vencido' || p.diasRestantes < 0).length;
    const prazosProximoMes = prazosAtivos.filter(p => p.urgencia === 'proximo_mes').length;
    
    setMetricas({
      hoje: prazosHoje,
      estaSemana: prazosEstaSemana,
      proximoMes: prazosProximoMes,
      concluidos: prazosConcluidos.length,
      total: totalPrazos,
      taxaCumprimento: taxaCumprimento,
      vencidos: prazosVencidos
    });

    // Simular métricas de comunicações para demonstração
    setMetricasComunicacoes({
      total: 51,
      processadas: totalPrazos,
      pendentes: 51 - totalPrazos,
      naoLidas: 15,
      comPrazo: totalPrazos
    });

    console.log('📊 Métricas Demo - Prazos:', {
      total: totalPrazos,
      vencidos: prazosVencidos,
      hoje: prazosHoje,
      estaSemana: prazosEstaSemana,
      proximoMes: prazosProximoMes,
      concluidos: prazosConcluidos.length
    });
    setLoading(false);
  };

  // 🚀 NOVA LÓGICA: Filtrar por ABA (Prazos vs Comunicações)
  const { comunicacoesFiltradas, totalPages, totalItems } = useMemo(() => {
    let filtered = [];

    // 📋 SEPARAR POR ABA PRIMEIRO
    if (abaAtiva === 'prazos') {
      // ABA PRAZOS: Só comunicações COM prazos PENDENTES
      filtered = comunicacoes.filter(c => {
        const prazoRelacionado = prazos.find(p => p.comunicacaoId === c.id);
        return prazoRelacionado && prazoRelacionado.status !== 'concluido';
      });

      // Filtros específicos para PRAZOS
      if (filtroAtivo === 'vencidos') {
        filtered = filtered.filter(c => {
          const prazoRelacionado = prazos.find(p => p.comunicacaoId === c.id);
          return prazoRelacionado?.urgencia === 'vencido';
        });
      } else if (filtroAtivo === 'hoje') {
        filtered = filtered.filter(c => {
          const prazoRelacionado = prazos.find(p => p.comunicacaoId === c.id);
          return prazoRelacionado?.urgencia === 'hoje';
        });
      } else if (filtroAtivo === 'esta_semana') {
        filtered = filtered.filter(c => {
          const prazoRelacionado = prazos.find(p => p.comunicacaoId === c.id);
          return prazoRelacionado?.urgencia === 'esta_semana';
        });
      }
      // 'todos' já está aplicado acima (todos os prazos pendentes)

    } else if (abaAtiva === 'comunicacoes') {
      // ABA COMUNICAÇÕES: Só comunicações SEM prazos NÃO VISTAS
      filtered = comunicacoes.filter(c => {
        const prazoRelacionado = prazos.find(p => p.comunicacaoId === c.id);
        return !prazoRelacionado && !c.isRead;
      });

      // Filtros específicos para COMUNICAÇÕES (menos opções)
      if (filtroAtivo === 'nao_lidas') {
        // Já filtrado acima (só não lidas)
        filtered = filtered;
      }
      // 'todos' já está aplicado acima (todas as comunicações não vistas)
    }

    // Busca por texto
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.numeroProcesso?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.conteudo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.assunto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.remetente?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 🎯 ORDENAÇÃO INTELIGENTE: Prazos SEMPRE primeiro, por urgência
    filtered.sort((a, b) => {
      const prazoA = prazos.find(p => p.comunicacaoId === a.id);
      const prazoB = prazos.find(p => p.comunicacaoId === b.id);
      
      // 1. PRAZOS SEMPRE VÊM PRIMEIRO (independente de tudo)
      if (prazoA && !prazoB) return -1;
      if (!prazoA && prazoB) return 1;
      
      // 2. Entre PRAZOS: ordenar por urgência (mais urgente primeiro)
      if (prazoA && prazoB) {
        const urgenciaOrder = { 'vencido': 0, 'hoje': 1, 'amanha': 2, 'esta_semana': 3, 'normal': 4, 'proximo_mes': 5 };
        const urgenciaA = urgenciaOrder[prazoA.urgencia] ?? 6;
        const urgenciaB = urgenciaOrder[prazoB.urgencia] ?? 6;
      
      if (urgenciaA !== urgenciaB) return urgenciaA - urgenciaB;
      
        // Se mesma urgência, ordenar por dias restantes (menor primeiro)
        const diasA = prazoA.diasRestantes ?? 999;
        const diasB = prazoB.diasRestantes ?? 999;
        return diasA - diasB;
      }
      
      // 3. Entre COMUNICAÇÕES INFORMATIVAS: não lidas primeiro
      if (!prazoA && !prazoB) {
        if (!a.isRead && b.isRead) return -1;
        if (a.isRead && !b.isRead) return 1;
        
        // Se mesmo status de leitura, ordenar por data (mais recente primeiro)
        const dateA = new Date(a.dataRecebimento || a.createdAt || 0);
        const dateB = new Date(b.dataRecebimento || b.createdAt || 0);
        return dateB - dateA;
      }
      
      return 0;
    });

    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Aplicar paginação
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return {
      comunicacoesFiltradas: paginatedItems,
      totalPages,
      totalItems
    };
  }, [comunicacoes, prazos, filtroAtivo, searchTerm, currentPage, itemsPerPage]);

  // Resetar página e filtro quando trocar de aba ou filtro mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [abaAtiva, filtroAtivo, searchTerm]);

  // Resetar filtro quando trocar de aba
  useEffect(() => {
    setFiltroAtivo('todos');
    setCurrentPage(1);
  }, [abaAtiva]);

  // 🚀 NOVOS HANDLERS para comunicações
  const handleMarcarComoVista = async (comunicacao) => {
    try {
      // Atualizar comunicação como lida
      await ComunicacaoService.marcarComoLida(comunicacao.id, currentUser.uid);
      
      // Recarregar comunicações
      const comunicacoesAtualizadas = await ComunicacaoService.getUserComunicacoes(currentUser.uid);
      setComunicacoes(comunicacoesAtualizadas);
      
      console.log(`✅ Comunicação ${comunicacao.id} marcada como vista`);
    } catch (error) {
      console.error('❌ Erro ao marcar comunicação como vista:', error);
    }
  };

  const handleVerDetalhesComunicacao = (comunicacao) => {
    // Para comunicações, usar o mesmo modal de detalhes
    // Criar um objeto "prazo" temporário para compatibilidade
    const prazoTemp = {
      id: comunicacao.id,
      numeroProcesso: comunicacao.numeroProcesso,
      tipo: 'comunicacao',
      descricao: comunicacao.assunto || 'Comunicação Processual',
      conteudo: comunicacao.conteudo,
      dataRecebimento: comunicacao.dataRecebimento,
      remetente: comunicacao.remetente,
      tribunal: comunicacao.tribunal,
      status: comunicacao.isRead ? 'vista' : 'nao_vista',
      urgencia: 'informativa',
      temPrazo: false
    };
    
    setPrazoSelecionado(prazoTemp);
    setShowPrazoDetails(true);
  };

  // Handlers originais para prazos
  const handleMarcarConcluido = async (prazo) => {
    try {
      await PrazoService.marcarPrazoConcluido(currentUser.uid, prazo);
      
      // Atualizar apenas o prazo específico localmente
      const prazosAtualizados = prazos.map(p => 
        p.id === prazo.id 
          ? { ...p, status: 'concluido', dataConclusao: new Date().toISOString() }
          : p
      );
      setPrazos(prazosAtualizados);
      
      // Recalcular TODAS as métricas com base nos prazos atualizados
      const prazosAtivos = prazosAtualizados.filter(p => p.status !== 'concluido');
      const prazosConcluidos = prazosAtualizados.filter(p => p.status === 'concluido');
      const total = prazosAtualizados.length;
      const taxaCumprimento = total > 0 ? Math.round((prazosConcluidos.length / total) * 100) : 0;
      
      setMetricas(prev => ({
        ...prev,
        hoje: prazosAtivos.filter(p => p.urgencia === 'hoje').length,
        estaSemana: prazosAtivos.filter(p => p.urgencia === 'esta_semana').length,
        proximoMes: prazosAtivos.filter(p => p.urgencia === 'proximo_mes').length,
        concluidos: prazosConcluidos.length,
        total: total,
        taxaCumprimento: taxaCumprimento,
        vencidos: prazosAtivos.filter(p => p.urgencia === 'vencido' || p.diasRestantes < 0).length
      }));
    } catch (error) {
      console.error('Erro ao marcar prazo como concluído:', error);
      alert('Erro ao marcar prazo como concluído: ' + error.message);
    }
  };

  const handleReverterConclusao = async (prazo) => {
    try {
      await PrazoService.reverterConclusaoPrazo(currentUser.uid, prazo);
      
      // Atualizar apenas o prazo específico localmente
      const prazosAtualizados = prazos.map(p => 
        p.id === prazo.id 
          ? { ...p, status: 'pendente', dataConclusao: null }
          : p
      );
      setPrazos(prazosAtualizados);
      
      // Recalcular TODAS as métricas com base nos prazos atualizados
      const prazosAtivos = prazosAtualizados.filter(p => p.status !== 'concluido');
      const prazosConcluidos = prazosAtualizados.filter(p => p.status === 'concluido');
      const total = prazosAtualizados.length;
      const taxaCumprimento = total > 0 ? Math.round((prazosConcluidos.length / total) * 100) : 0;
      
      setMetricas(prev => ({
        ...prev,
        hoje: prazosAtivos.filter(p => p.urgencia === 'hoje').length,
        estaSemana: prazosAtivos.filter(p => p.urgencia === 'esta_semana').length,
        proximoMes: prazosAtivos.filter(p => p.urgencia === 'proximo_mes').length,
        concluidos: prazosConcluidos.length,
        total: total,
        taxaCumprimento: taxaCumprimento,
        vencidos: prazosAtivos.filter(p => p.urgencia === 'vencido' || p.diasRestantes < 0).length
      }));
    } catch (error) {
      console.error('Erro ao reverter conclusão:', error);
      alert('Erro ao reverter conclusão: ' + error.message);
    }
  };

  const handleVerDetalhes = (prazo) => {
    setPrazoSelecionado(prazo);
    setShowPrazoDetails(true);
  };

  const handleEditarPrazo = (prazo) => {
    setPrazoSelecionado(prazo);
    setShowEditarPrazo(true);
  };

  const handleSalvarEdicaoPrazo = async (prazoAtualizado) => {
    try {
      await PrazoService.editarPrazo(currentUser.uid, prazoAtualizado);
      
      // Atualizar apenas o prazo específico localmente
      setPrazos(prev => prev.map(p => 
        p.id === prazoAtualizado.id ? prazoAtualizado : p
      ));
      
      setShowEditarPrazo(false);
      setPrazoSelecionado(null);
    } catch (error) {
      console.error('Erro ao salvar edição do prazo:', error);
      alert('Erro ao salvar edição: ' + error.message);
    }
  };

  const handleDownloadPDF = (prazo) => {
    try {
      // Criar conteúdo do PDF
      const conteudo = `
DETALHES DO PRAZO - ${APP_CONFIG.name}

Processo: ${prazo.numeroProcesso}
Cliente: ${prazo.cliente || 'N/A'}
Tribunal: ${prazo.tribunal}
Tipo: ${prazo.tipo}
Tipo de Ato: ${prazo.tipoAto || 'N/A'}

Data de Publicação: ${prazo.dataPublicacao ? new Date(prazo.dataPublicacao).toLocaleDateString('pt-BR') : 'N/A'}
Data de Vencimento: ${new Date(prazo.dataVencimento).toLocaleDateString('pt-BR')}
Prazo Total: ${prazo.dias} dias${prazo.diasUteis ? ' úteis' : ''}
Dias Restantes: ${prazo.diasRestantes < 0 ? `${Math.abs(prazo.diasRestantes)} dias em atraso` : `${prazo.diasRestantes} dias`}

Descrição: ${prazo.descricao || 'N/A'}
Observações: ${prazo.observacoes || 'Nenhuma observação'}

CONTEÚDO DA COMUNICAÇÃO:
${prazo.textoCompleto || 'Conteúdo não disponível'}

AÇÕES SUGERIDAS:
${prazo.acoesSugeridas?.map((acao, i) => `${i + 1}. ${acao}`).join('\n') || 'Nenhuma ação sugerida'}

---
Gerado em: ${new Date().toLocaleString('pt-BR')}
Sistema: ${APP_CONFIG.name}
      `.trim();

      // Criar e baixar arquivo
      const blob = new Blob([conteudo], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prazo_${prazo.numeroProcesso.replace(/[^0-9]/g, '')}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar arquivo: ' + error.message);
    }
  };

  const handleSyncManual = () => {
    setShowSyncModal(true);
  };

  const handleSyncComplete = async (result) => {
    // Recarregar dados após sincronização bem-sucedida
    if (currentUser?.uid && !currentUser.isDemoUserManual) {
      await loadDashboardData();
    }
  };

  // 🤖 Handler para processar comunicações automaticamente 
  const handleProcessarComunicacoes = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoadingSync(true);
      console.log('🤖 Processando comunicações automaticamente...');

      const resultado = await ComunicacaoService.processarComunicacoesAutomaticamente(currentUser.uid);

      // Limpar cache para forçar recarregamento
      const { default: cacheService } = await import('../services/cacheService');
      cacheService.clear(); // Limpa todo o cache
      
      // Recarregar todos os dados após processamento
      await loadDashboardData();

      let mensagem = `🎉 Processamento concluído!\n\n`;
      mensagem += `✅ ${resultado.processadas} comunicações analisadas\n`;
      mensagem += `📅 ${resultado.prazosGerados || 0} prazos detectados pela IA\n`;
      
      if (resultado.restantes > 0) {
        mensagem += `\n⏳ Ainda restam ${resultado.restantes} comunicações.\nClique novamente para processar mais.`;
      } else {
        mensagem += `\n✨ Todas as comunicações foram processadas!`;
      }
      
      alert(mensagem);
      
    } catch (error) {
      console.error('❌ Erro no processamento automático:', error);
      alert('Erro no processamento: ' + error.message);
    } finally {
      setLoadingSync(false);
    }
  };

  // Handlers do Onboarding
  const handleOnboardingComplete = async () => {
    console.log('✅ Onboarding concluído com sucesso!');
    setShowOnboarding(false);
    
    // Aguardar um momento e abrir automaticamente o modal de sincronização
    // para o usuário fazer a primeira busca
    setTimeout(() => {
      console.log('🔄 Abrindo sincronização automática após onboarding...');
      setShowSyncModal(true);
    }, 500);
  };

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
  };

  const handleNeverShowOnboarding = () => {
    if (currentUser?.uid) {
      localStorage.setItem(`onboarding_never_show_${currentUser.uid}`, 'true');
      console.log('🙈 Usuário optou por não ver mais o onboarding');
    }
    setShowOnboarding(false);
  };

  // Handler para forçar conclusão do onboarding (desenvolvimento)
  const handleForcarConclusaoOnboarding = async () => {
    if (!currentUser?.uid) return;

    try {
      console.log('🔧 Forçando conclusão do onboarding...');

      // Marcar onboarding como concluído
      await FirebaseService.updateUserProfile(currentUser.uid, {
        onboardingCompleted: true
      });

      // Inicializar sincronização automática
      await SyncService.initializeUserSync(currentUser.uid);

      console.log('✅ Onboarding concluído e sincronização inicializada');
      alert('✅ Onboarding concluído! Sincronização automática inicializada.');

    } catch (error) {
      console.error('❌ Erro ao forçar conclusão do onboarding:', error);
      alert('Erro: ' + error.message);
    }
  };

  // 🎯 FILTROS DE URGÊNCIA - SEMPRE VISÍVEIS (segurança para o advogado)
  const getFiltrosParaAba = () => {
    // Sempre mostra os filtros de PRAZOS, independente da aba
    // Isso garante que o advogado nunca esqueça que tem prazos urgentes
    const prazosAtivos = comunicacoes.filter(c => {
      const prazo = prazos.find(p => p.comunicacaoId === c.id);
      return prazo && prazo.status !== 'concluido';
    });
    
    return [
      { key: 'todos', label: 'Todos', count: prazosAtivos.length, icon: '📋' },
      { key: 'vencidos', label: 'Vencidos', count: metricas.vencidos || 0, color: 'red', icon: '🚨' },
      { key: 'hoje', label: 'Hoje', count: metricas.hoje || 0, color: 'red', icon: '⏰' },
      { key: 'esta_semana', label: 'Esta Semana', count: metricas.estaSemana || 0, color: 'yellow', icon: '📅' }
    ];
  };

  const filtros = getFiltrosParaAba();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-blue-900 font-medium">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Conteúdo do Dashboard */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {/* Métricas Compactas */}
        <section className="mb-4 md:mb-6">
          <div className="grid grid-cols-5 gap-2 md:gap-3">
            {/* Vencidos */}
            <div className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
              (metricas.vencidos || 0) > 0 
                ? 'bg-red-50 border-red-300 hover:border-red-400' 
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-lg">⚠️</span>
                <span className={`text-2xl font-bold ${(metricas.vencidos || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {metricas.vencidos || 0}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">Vencidos</p>
            </div>

            {/* Hoje */}
            <div className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
              (metricas.hoje || 0) > 0 
                ? 'bg-orange-50 border-orange-300 hover:border-orange-400' 
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-lg">🔥</span>
                <span className={`text-2xl font-bold ${(metricas.hoje || 0) > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                  {metricas.hoje || 0}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">Hoje</p>
            </div>

            {/* Esta Semana */}
            <div className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
              (metricas.estaSemana || 0) > 0 
                ? 'bg-amber-50 border-amber-300 hover:border-amber-400' 
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-lg">📅</span>
                <span className={`text-2xl font-bold ${(metricas.estaSemana || 0) > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                  {metricas.estaSemana || 0}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">Esta Semana</p>
            </div>

            {/* Total */}
            <div className="p-3 rounded-lg border-2 bg-slate-50 border-slate-200 hover:border-slate-300 transition-all cursor-pointer">
              <div className="flex items-center justify-between">
                <span className="text-lg">📋</span>
                <span className="text-2xl font-bold text-slate-700">
                  {metricasComunicacoes.total || 0}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Comunicações</p>
            </div>

            {/* Concluídos */}
            <div className="p-3 rounded-lg border-2 bg-emerald-50 border-emerald-200 hover:border-emerald-300 transition-all cursor-pointer">
              <div className="flex items-center justify-between">
                <span className="text-lg">✅</span>
                <span className="text-2xl font-bold text-emerald-600">
                  {metricas.concluidos || 0}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Concluídos</p>
            </div>
          </div>
        </section>

        {/* ALERTA DE URGÊNCIA - Prazos Vencidos ou Hoje */}
        {((metricas.vencidos || 0) > 0 || (metricas.hoje || 0) > 0) && (
          <section className="mb-4">
            <div className={`rounded-xl p-4 border-2 ${
              (metricas.vencidos || 0) > 0 
                ? 'bg-red-50 border-red-300' 
                : 'bg-orange-50 border-orange-300'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${(metricas.vencidos || 0) > 0 ? 'bg-red-100' : 'bg-orange-100'}`}>
                  <AlertTriangle className={`w-6 h-6 ${(metricas.vencidos || 0) > 0 ? 'text-red-600' : 'text-orange-600'}`} />
                </div>
                <div className="flex-1">
                  <h3 className={`font-bold ${(metricas.vencidos || 0) > 0 ? 'text-red-800' : 'text-orange-800'}`}>
                    ⚠️ Atenção Imediata
                  </h3>
                  <p className={`text-sm ${(metricas.vencidos || 0) > 0 ? 'text-red-700' : 'text-orange-700'}`}>
                    {(metricas.vencidos || 0) > 0 && `${metricas.vencidos} prazo(s) vencido(s)`}
                    {(metricas.vencidos || 0) > 0 && (metricas.hoje || 0) > 0 && ' • '}
                    {(metricas.hoje || 0) > 0 && `${metricas.hoje} vence(m) hoje`}
                  </p>
                </div>
                <button
                  onClick={() => setFiltroAtivo((metricas.vencidos || 0) > 0 ? 'vencidos' : 'hoje')}
                  className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
                    (metricas.vencidos || 0) > 0 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
                >
                  Ver Agora
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Alerta: Comunicações aguardando IA */}
        {metricasComunicacoes.pendentes > 0 && (
          <section className="mb-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-100">
                  <span className="text-xl">🤖</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-indigo-900">
                    {metricasComunicacoes.pendentes} comunicações aguardando análise
                  </h3>
                  <p className="text-sm text-indigo-600">
                    {metricasComunicacoes.processadas || 0} processadas • {metricasComunicacoes.comPrazo || 0} com prazos
                  </p>
                </div>
                <button
                  onClick={handleProcessarComunicacoes}
                  disabled={loadingSync}
                  className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {loadingSync ? '⏳ Processando...' : '🧠 Processar com IA'}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Status Simples */}
        <section className="mb-6 md:mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <span className="text-xl">📄</span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Comunicações Analisadas</h2>
                  <p className="text-sm text-slate-500">
                    {metricasComunicacoes.total || 0} comunicações • {metricasComunicacoes.comPrazo || 0} com prazos
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => onNavigate('comunicacoes')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Ver Detalhes →
              </button>
            </div>
          </div>
        </section>

        {/* Controles de Filtro - SEMPRE VISÍVEL (lembrete de urgência) */}
        <section className="mb-6 md:mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              {/* Filtros Modernos com Cores por Urgência */}
              <div className="flex flex-wrap gap-2 md:gap-3">
                {filtros.map(filtro => {
                  // Definir cores por tipo de filtro
                  const getFilterColors = () => {
                    if (filtroAtivo === filtro.key) {
                      switch (filtro.key) {
                        case 'vencidos':
                          return 'bg-red-600 text-white border-red-600 shadow-md';
                        case 'hoje':
                          return 'bg-orange-500 text-white border-orange-500 shadow-md';
                        case 'esta_semana':
                          return 'bg-amber-500 text-white border-amber-500 shadow-md';
                        default:
                          return 'bg-slate-800 text-white border-slate-800 shadow-md';
                      }
                    }
                    switch (filtro.key) {
                      case 'vencidos':
                        return filtro.count > 0 
                          ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100 hover:border-red-400' 
                          : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50';
                      case 'hoje':
                        return filtro.count > 0 
                          ? 'bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100 hover:border-orange-400' 
                          : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50';
                      case 'esta_semana':
                        return filtro.count > 0 
                          ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:border-amber-400' 
                          : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50';
                      default:
                        return 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400';
                    }
                  };

                  const getBadgeColors = () => {
                    if (filtroAtivo === filtro.key) return 'bg-white/90 text-slate-800';
                    switch (filtro.key) {
                      case 'vencidos': return filtro.count > 0 ? 'bg-red-200 text-red-800 animate-pulse' : 'bg-slate-100 text-slate-500';
                      case 'hoje': return filtro.count > 0 ? 'bg-orange-200 text-orange-800' : 'bg-slate-100 text-slate-500';
                      case 'esta_semana': return filtro.count > 0 ? 'bg-amber-200 text-amber-800' : 'bg-slate-100 text-slate-500';
                      default: return 'bg-slate-200 text-slate-700';
                    }
                  };

                  return (
                    <button
                      key={filtro.key}
                      onClick={() => {
                        setFiltroAtivo(filtro.key);
                        setAbaAtiva('prazos'); // Volta para aba de prazos ao clicar em filtro
                      }}
                      className={`
                        inline-flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm font-semibold rounded-lg border-2 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-md
                        ${getFilterColors()}
                        ${filtro.key === 'vencidos' && filtro.count > 0 && filtroAtivo !== 'vencidos' ? 'animate-pulse' : ''}
                      `}
                    >
                      <span>{filtro.icon}</span>
                      {filtro.label}
                      <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${getBadgeColors()}`}>
                        {filtro.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Busca Responsiva */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 lg:flex-none">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Buscar prazos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full lg:w-64 pl-10 pr-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Abas de Prazos vs Comunicações */}
        <section>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {/* Abas - Design Profissional */}
            <div className="border-b border-slate-200 bg-slate-50">
              <div className="flex">
                <button
                  onClick={() => setAbaAtiva('prazos')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-all border-b-2 ${
                    abaAtiva === 'prazos'
                      ? 'border-indigo-600 text-indigo-700 bg-white'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>⚠️ Prazos</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      abaAtiva === 'prazos' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {comunicacoes.filter(c => {
                        const prazo = prazos.find(p => p.comunicacaoId === c.id);
                        return prazo && prazo.status !== 'concluido';
                      }).length}
                    </span>
                  </div>
                </button>
                
                <button
                  onClick={() => setAbaAtiva('calendario')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-all border-b-2 ${
                    abaAtiva === 'calendario'
                      ? 'border-indigo-600 text-indigo-700 bg-white'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>📅 Calendário</span>
                  </div>
                </button>

                <button
                  onClick={() => setAbaAtiva('comunicacoes')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-all border-b-2 ${
                    abaAtiva === 'comunicacoes'
                      ? 'border-indigo-600 text-indigo-700 bg-white'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>📄 Sem Prazo</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      abaAtiva === 'comunicacoes' 
                        ? 'bg-indigo-100 text-indigo-700' 
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {comunicacoes.filter(c => {
                        const prazo = prazos.find(p => p.comunicacaoId === c.id);
                        return !prazo && !c.isRead;
                      }).length}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Barra de Info e Controles */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">{totalItems}</span> itens • Página {currentPage} de {totalPages}
                </p>
                
                <div className="flex items-center gap-2">
                  {abaAtiva === 'comunicacoes' && (
                    <button
                      onClick={() => setShowDateView(!showDateView)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        showDateView 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      <Calendar className="w-3 h-3" />
                      {showDateView ? 'Lista' : 'Por Data'}
                    </button>
                  )}
                  
                  {syncStats && syncStats.diasSemSync > 0 && (
                    <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      <AlertCircle className="w-3 h-3" />
                      {syncStats.diasSemSync}d sem sync
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Conteúdo da Lista */}
            <div className="p-6">
              {/* Aba Calendário */}
              {abaAtiva === 'calendario' ? (
                <CalendarioPrazos
                  prazos={prazos}
                  onDateClick={(date, prazosData) => {
                    console.log('Data clicada:', date, prazosData);
                  }}
                  onPrazoClick={handleVerDetalhes}
                />
              ) : abaAtiva === 'comunicacoes' && showDateView ? (
                <DateFilterView
                  comunicacoes={comunicacoes}
                  onComunicacaoSelect={(comunicacao) => {
                    setPrazoSelecionado(comunicacao);
                    setShowPrazoDetails(true);
                  }}
                />
              ) : comunicacoesFiltradas.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                    <AlertCircle size={32} className="text-blue-500" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-3">
                    Nenhuma comunicação encontrada
                  </h3>
                  <p className="text-blue-600 font-medium">
                    {filtroAtivo === 'todos'
                      ? 'Não há comunicações sincronizadas ainda.'
                      : `Não há comunicações para o filtro "${filtros.find(f => f.key === filtroAtivo)?.label}".`
                    }
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {comunicacoesFiltradas.map(comunicacao => {
                      // Buscar prazo relacionado se existir
                      const prazoRelacionado = prazos.find(p => p.comunicacaoId === comunicacao.id);
                      
                      // Se tem prazo, usar o PrazoCard original
                      if (prazoRelacionado) {
                        return (
                      <PrazoCard
                            key={prazoRelacionado.id}
                            prazo={prazoRelacionado}
                        onMarcarConcluido={handleMarcarConcluido}
                        onReverterConclusao={handleReverterConclusao}
                        onVerDetalhes={handleVerDetalhes}
                        onEditarPrazo={handleEditarPrazo}
                      />
                        );
                      } else {
                        // Se não tem prazo, usar um card simples para comunicação
                        return (
                          <ComunicacaoCard
                            key={comunicacao.id}
                            comunicacao={comunicacao}
                            prazoRelacionado={null}
                            onMarcarComoVista={() => handleMarcarComoVista(comunicacao)}
                            onVerDetalhes={() => handleVerDetalhesComunicacao(comunicacao)}
                          />
                        );
                      }
                    })}
                  </div>

                  {/* Paginação Moderna */}
                  <div className="mt-8 pt-6 border-t border-blue-200">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setItemsPerPage}
                      showItemsPerPage={true}
                      showInfo={true}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Modal do Calendário */}
      <Modal
        isOpen={showCalendario}
        onClose={() => setShowCalendario(false)}
        title="Calendário de Prazos"
        size="xl"
      >
        <CalendarioPrazos
          prazos={prazos}
          onDateClick={(date, prazosData) => {
            console.log('Data clicada:', date, prazosData);
          }}
          onPrazoClick={handleVerDetalhes}
        />
      </Modal>

      {/* Modal de Detalhes do Prazo */}
      <Modal
        isOpen={showPrazoDetails}
        onClose={() => setShowPrazoDetails(false)}
        title="Detalhes Completos do Prazo"
        size="4xl"
      >
        {prazoSelecionado && (
          <div className="space-y-6">
            {/* Cabeçalho Moderno e Bonito */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-blue-600 rounded-lg">
                  <FileText size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-xl">
                    📄 {prazoSelecionado.tipo?.charAt(0).toUpperCase() + prazoSelecionado.tipo?.slice(1) || 'Comunicação'}
              </h3>
                  <p className="text-blue-700 font-mono text-lg">{prazoSelecionado.numeroProcesso}</p>
                </div>
              </div>
              {prazoSelecionado.tipoAto && (
                <div className="bg-blue-200 px-4 py-2 rounded-lg">
                  <p className="text-blue-800 font-semibold">
                    🏛️ {prazoSelecionado.tipoAto.charAt(0).toUpperCase() + prazoSelecionado.tipoAto.slice(1)}
                </p>
                </div>
              )}
            </div>

            {/* Informações principais com cards coloridos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {prazoSelecionado.cliente && (
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <User size={20} className="text-slate-600" />
              <div>
                    <p className="text-xs text-slate-500 font-medium">Cliente</p>
                    <p className="text-sm font-semibold text-slate-900">{prazoSelecionado.cliente}</p>
              </div>
                </div>
              )}
              
              {prazoSelecionado.tribunal && (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <ExternalLink size={20} className="text-emerald-600" />
              <div>
                    <p className="text-xs text-emerald-600 font-medium">Tribunal</p>
                    <p className="text-sm font-semibold text-slate-900">{prazoSelecionado.tribunal}</p>
              </div>
                </div>
              )}
              
              {prazoSelecionado.dataPublicacao && (
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Calendar size={20} className="text-green-600" />
              <div>
                    <p className="text-xs text-green-600 font-medium">Publicado em</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {new Date(prazoSelecionado.dataPublicacao).toLocaleDateString('pt-BR')}
                </p>
              </div>
                </div>
              )}
              
              {prazoSelecionado.dataVencimento && (
                <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <Calendar size={20} className="text-orange-600" />
              <div>
                    <p className="text-xs text-orange-600 font-medium">Vence em</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {prazoSelecionado.dataVencimento ? new Date(prazoSelecionado.dataVencimento).toLocaleDateString('pt-BR') : 'N/A'}
                </p>
              </div>
                </div>
              )}
              
              {prazoSelecionado.dias && (
                <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Clock size={20} className="text-purple-600" />
              <div>
                    <p className="text-xs text-purple-600 font-medium">Prazo Total</p>
                    <p className="text-sm font-semibold text-slate-900">
                  {prazoSelecionado.dias} dias{prazoSelecionado.diasUteis ? ' úteis' : ''}
                </p>
              </div>
                </div>
              )}
              
              {prazoSelecionado.diasRestantes !== undefined && (
                <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                  prazoSelecionado.diasRestantes < 0 ? 'bg-red-50 border-red-200' :
                  prazoSelecionado.diasRestantes === 0 ? 'bg-amber-50 border-amber-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <AlertTriangle size={20} className={
                    prazoSelecionado.diasRestantes < 0 ? 'text-red-600' :
                    prazoSelecionado.diasRestantes === 0 ? 'text-amber-600' :
                    'text-blue-600'
                  } />
              <div>
                    <p className={`text-xs font-medium ${
                  prazoSelecionado.diasRestantes < 0 ? 'text-red-600' :
                  prazoSelecionado.diasRestantes === 0 ? 'text-amber-600' :
                      'text-blue-600'
                    }`}>Dias Restantes</p>
                    <p className="text-sm font-semibold text-slate-900">
                  {prazoSelecionado.diasRestantes < 0
                    ? `${Math.abs(prazoSelecionado.diasRestantes)} dias em atraso`
                    : prazoSelecionado.diasRestantes === 0
                    ? 'Vence hoje'
                    : `${prazoSelecionado.diasRestantes} dias`
                  }
                </p>
              </div>
                </div>
              )}
            </div>

            {/* Descrição */}
            {prazoSelecionado.descricao && (
              <div>
                <span className="font-medium text-gray-700">Descrição:</span>
                <p className="text-gray-900 mt-1">{prazoSelecionado.descricao}</p>
              </div>
            )}

            {/* Observações */}
            {prazoSelecionado.observacoes && (
              <div>
                <span className="font-medium text-gray-700">Observações:</span>
                <p className="text-gray-900 italic mt-1">{prazoSelecionado.observacoes}</p>
              </div>
            )}

            {/* Conteúdo da comunicação - Melhorado */}
            {(prazoSelecionado.textoCompleto || prazoSelecionado.conteudo) && (
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    📖 Conteúdo da Publicação
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const texto = prazoSelecionado.textoCompleto || prazoSelecionado.conteudo || '';
                        navigator.clipboard.writeText(texto);
                        // Feedback visual
                        const button = document.getElementById('copy-btn-details');
                        if (button) {
                          const originalText = button.textContent;
                          button.textContent = '✅ Copiado!';
                          setTimeout(() => {
                            button.textContent = originalText;
                          }, 2000);
                        }
                      }}
                      id="copy-btn-details"
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Copy size={16} />
                      Copiar Texto
                    </button>
                    <button
                      onClick={() => {
                        const printContent = `
                          <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px;">
                            <h1 style="color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">
                              📄 Conteúdo da Publicação
                            </h1>
                            
                            <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;">
                              <p><strong>📋 Processo:</strong> ${prazoSelecionado.numeroProcesso || 'N/A'}</p>
                              ${prazoSelecionado.cliente ? `<p><strong>👤 Cliente:</strong> ${prazoSelecionado.cliente}</p>` : ''}
                              ${prazoSelecionado.dataPublicacao ? `<p><strong>📅 Publicado em:</strong> ${new Date(prazoSelecionado.dataPublicacao).toLocaleDateString('pt-BR')}</p>` : ''}
                              ${prazoSelecionado.dataVencimento ? `<p><strong>⏰ Vence em:</strong> ${new Date(prazoSelecionado.dataVencimento).toLocaleDateString('pt-BR')}</p>` : ''}
                              ${prazoSelecionado.tribunal ? `<p><strong>🏛️ Tribunal:</strong> ${prazoSelecionado.tribunal}</p>` : ''}
                            </div>
                            
                            <div style="background: white; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px;">
                              <h2 style="color: #374151; margin-bottom: 15px;">📖 Conteúdo:</h2>
                              <div style="white-space: pre-wrap; line-height: 1.8; color: #374151;">
                                ${(prazoSelecionado.textoCompleto || prazoSelecionado.conteudo || '').replace(/\n/g, '<br>')}
                              </div>
                            </div>
                            
                            <div style="text-align: center; margin-top: 30px; color: #64748b; font-size: 12px;">
                              <p>Gerado pelo JurisPrazo em ${new Date().toLocaleString('pt-BR')}</p>
                            </div>
                          </div>
                        `;

                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>Publicação - ${prazoSelecionado.numeroProcesso}</title>
                              <style>
                                @media print {
                                  body { margin: 0; }
                                  @page { margin: 1cm; }
                                }
                              </style>
                            </head>
                            <body>
                              ${printContent}
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        printWindow.print();
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
                    >
                      <Printer size={16} />
                      Imprimir
                    </button>
                    <button
                      onClick={() => setConteudoExpandido(!conteudoExpandido)}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm font-medium"
                    >
                      {conteudoExpandido ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                      {conteudoExpandido ? 'Minimizar' : 'Expandir'}
                    </button>
                  </div>
                </div>
                <div className={`bg-white p-6 rounded-xl border border-slate-200 overflow-y-auto transition-all ${
                  conteudoExpandido ? 'max-h-96' : 'max-h-60'
                }`}>
                  <div 
                    className="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm"
                    style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                  >
                    {prazoSelecionado.textoCompleto || prazoSelecionado.conteudo || 'Nenhum conteúdo disponível.'}
                </div>
                </div>
                {!conteudoExpandido && (prazoSelecionado.textoCompleto || prazoSelecionado.conteudo)?.length > 300 && (
                  <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    💡 <strong>Dica:</strong> Use Ctrl+F para pesquisar no conteúdo ou clique em "Expandir" para ver tudo
                  </div>
                )}
              </div>
            )}

            {/* Ações sugeridas */}
            {prazoSelecionado.acoesSugeridas?.length > 0 && (
              <div>
                <span className="font-medium text-gray-700">Ações Sugeridas pela IA:</span>
                <ul className="list-disc list-inside text-gray-900 mt-1 space-y-1">
                  {prazoSelecionado.acoesSugeridas.map((acao, index) => (
                    <li key={index}>{acao}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags */}
            {prazoSelecionado.tags?.length > 0 && (
              <div>
                <span className="font-medium text-gray-700">Tags:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {prazoSelecionado.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-200 text-gray-900 text-xs rounded border border-gray-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadados */}
            <div className="border-t border-gray-200 pt-4 text-xs text-gray-700">
              <div className="flex justify-between">
                <span>
                  {prazoSelecionado.analisadoPorIA ? '🤖 Processado por IA' : '📝 Cadastrado manualmente'}
                </span>
                <span>
                  {prazoSelecionado.dataAnalise
                    ? `Analisado em ${new Date(prazoSelecionado.dataAnalise).toLocaleDateString('pt-BR')}`
                    : ''
                  }
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Editar Prazo */}
      <EditarPrazoModal
        isOpen={showEditarPrazo}
        onClose={() => {
          setShowEditarPrazo(false);
          setPrazoSelecionado(null);
        }}
        prazo={prazoSelecionado}
        onSalvar={handleSalvarEdicaoPrazo}
        loading={loadingSync}
      />

      {/* Onboarding Wizard */}
      <OnboardingWizard
        isOpen={showOnboarding}
        onClose={handleOnboardingClose}
        onComplete={handleOnboardingComplete}
        currentUser={currentUser}
        onNeverShowAgain={handleNeverShowOnboarding}
      />

      {/* Modal de Sincronização Manual */}
      <SyncModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        currentUser={currentUser}
        onSyncComplete={handleSyncComplete}
      />
    </>
  );
};

export default DashboardPage;

